/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { PersonalInfoForm } from '@/components/ui/services/booking/PersonalInfoForm';
import { WorkshopPaymentSummary } from './WorkshopPaymentSummary';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/services/Badge';
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CalendarExport } from '@/components/ui/services/booking/CalendarExport';
import { sendToHubSpot } from '@/utils/hubspot';

interface WorkshopBookingFlowProps {
	isOpen: boolean;
	onClose: () => void;
	workshop: {
		_id: string;
		day: string;
		month: string;
		year: string;
		title: string;
		date: string;
		time: string;
		location: string;
		variant: 'dark' | 'light';
		calendarIconUrl: string;
		price: string;
		limit: number;
		image?: string;
		description?: string;
		benefits?: string[];
		duration?: number; // Duration in hours
	};
}

type BookingStep = 'personal' | 'payment' | 'payment-result' | 'confirmation';

export function WorkshopBookingFlow({
	isOpen,
	onClose,
	workshop,
}: WorkshopBookingFlowProps) {
	const [step, setStep] = React.useState<BookingStep>('personal');
	const [bookingData, setBookingData] = React.useState({
		fullName: '',
		email: '',
		phone: '',
		currentRole: '',
		coachingReason: '',
		challenges: '',
		publicSpeakingGoal: '',
		location: {
			city: '',
			state: '',
			country: '',
		},
	});
	const [bookingConfirmed, setBookingConfirmed] = React.useState(false);
	const [calendarEventCreated, setCalendarEventCreated] = React.useState(false);
	const [paymentStatus, setPaymentStatus] = React.useState<
		'idle' | 'pending' | 'success' | 'failed'
	>('idle');
	const [paymentError, setPaymentError] = React.useState<string | null>(null);

	// Convert workshop date and time to Date object for calendar
	const getWorkshopDate = () => {
		try {
			// Use the ISO date string from workshop.date as the base
			const baseDate = new Date(workshop.date);

			// Parse the start time from the time string (format: "10:00 - 12:00")
			const timeStart = workshop.time.split(' - ')[0].trim();
			const timeMatch = timeStart.match(/(\d+):(\d+)/);

			if (timeMatch) {
				const [_, hours, minutes] = timeMatch;
				const hour = parseInt(hours);
				const minute = parseInt(minutes);

				// Set the correct time on the base date
				baseDate.setHours(hour, minute, 0, 0);

				return baseDate;
			}

			// If time parsing fails, just return the base date
			return baseDate;
		} catch (error) {
			console.error('Error parsing workshop date:', error);
			// Return current date as fallback
			return new Date();
		}
	};

	// Extract workshop duration in minutes - improved reliability
	const getWorkshopDuration = () => {
		// If explicit duration is provided, convert from hours to minutes
		if (workshop.duration) return workshop.duration * 60;

		// Otherwise try to calculate from time string
		try {
			const [startTime, endTime] = workshop.time.split(' - ');

			// Helper function to convert time to minutes since midnight
			const timeToMinutes = (timeStr: string) => {
				const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
				if (!match) return null;

				const [_, hours, minutes, period] = match;
				let hour = parseInt(hours);
				if (period.toUpperCase() === 'PM' && hour < 12) hour += 12;
				if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;

				return hour * 60 + parseInt(minutes);
			};

			const startMinutes = timeToMinutes(startTime);
			const endMinutes = timeToMinutes(endTime);

			if (startMinutes !== null && endMinutes !== null) {
				return endMinutes - startMinutes;
			}
		} catch (error) {
			console.error('Error calculating workshop duration:', error);
		}

		// Default duration if calculation fails
		return 120; // 2 hours default
	};

	// Add a helper for formatting the date as YYYY-MM-DD
	const formatLocalDateString = (date: Date): string =>
		`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

	// Send booking data to HubSpot
	const sendPersonalInfoToHubSpot = async () => {
		try {
			await sendToHubSpot(
				{
					properties: [
						{ property: 'firstname', value: bookingData.fullName },
						{ property: 'email', value: bookingData.email },
						{ property: 'phone', value: bookingData.phone },
						{ property: 'city', value: bookingData?.location?.city },
						{ property: 'state', value: bookingData?.location?.state },
						{ property: 'country', value: bookingData?.location?.country },
						{ property: 'attended_workshop', value: workshop.title },
					],
				},
				6, // Booking Leads ID (Hubspot)
			);
		} catch (error) {
			console.error('Failed to send data to HubSpot:', error);
		}
	};

	// Handle payment result - improved error handling
	const handlePaymentResult = async (success: boolean, message?: string) => {
		try {
			setPaymentStatus(success ? 'success' : 'failed');

			if (message) {
				setPaymentError(message);
			}

			// Move to payment result step first
			setStep('payment-result');

			// If payment was successful, create calendar event
			if (success && bookingData.email) {
				await createCalendarEvent();

				// Only send to HubSpot and admin email if calendar creation was successful
				await sendPersonalInfoToHubSpot();
				await sendAdminAlertEmail();
			}
		} catch (error) {
			console.error('Failed to process payment result:', error);
			setPaymentStatus('failed');
			setPaymentError('Failed to process payment result');
			setStep('payment-result');
		}
	};

	// Extracted calendar event creation to a separate function for better organization
	const createCalendarEvent = async () => {
		try {
			const workshopDate = getWorkshopDate();
			const duration = getWorkshopDuration(); // Get properly calculated duration in minutes

			const baseurl =
				process.env.NODE_ENV === 'production'
					? 'https://yaap.co.za/.netlify/functions'
					: '/.netlify/functions';

			// Format date and timeSlot for create-event
			const formattedDate = formatLocalDateString(workshopDate);
			const formattedTime = `${String(workshopDate.getHours()).padStart(2, '0')}:${String(workshopDate.getMinutes()).padStart(2, '0')}`;

			const response = await fetch(`${baseurl}/create-event`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					title: `Yaap Workshop: ${workshop.title}`,
					description: `${workshop.description || workshop.title}\n\nAttendee: ${bookingData.fullName}\nPhone: ${bookingData.phone}`,
					date: formattedDate,
					timeSlot: formattedTime,
					attendeeEmail: bookingData.email,
					duration: duration, // Use the calculated duration
					type: 'workshop',
					workshopId: workshop._id,
				}),
			});

			const result = await response.json();
			setCalendarEventCreated(result.success);
			return result.success;
		} catch (error) {
			console.error('Failed to create calendar event:', error);
			setCalendarEventCreated(false);
			return false;
		}
	};

	// Send admin alert email
	const sendAdminAlertEmail = async () => {
		const baseurl =
			process.env.NODE_ENV === 'production'
				? 'https://yaap.co.za/.netlify/functions'
				: '/.netlify/functions';

		const workshopDate = `${workshop.day} ${workshop.month} ${workshop.year}`;

		const emailBody = `
        <p>A new workshop booking has been made:</p>
        <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif;">
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Workshop:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${workshop.title}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Date:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${workshopDate}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Time:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${workshop.time}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Location:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${workshop.location}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Full Name:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.fullName}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Email:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.email}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Phone:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.phone}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Address:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.location.city}, ${bookingData.location.state}, ${bookingData.location.country}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Current Role:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.currentRole || 'N/A'}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Coaching Reason:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.coachingReason || 'N/A'}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Challenges:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.challenges || 'N/A'}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Public Speaking Goal:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${bookingData.publicSpeakingGoal || 'N/A'}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Price:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">R${workshop.price}</td>
            </tr>
            <tr>
                <td style="border: 1px solid #ddd; padding: 8px;"><strong>Duration:</strong></td>
                <td style="border: 1px solid #ddd; padding: 8px;">${getWorkshopDuration()} minutes</td>
            </tr>
        </table>
    `;

		try {
			await fetch(`${baseurl}/email-service`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					type: 'sendEmail',
					to: 'bbndaba@yaap.co.za',
					cc: 'info@yaap.co.za',
					subject: 'Yaap Website: New Workshop Booking',
					body: emailBody,
				}),
			});
		} catch (error) {
			console.error('Failed to send admin alert email:', error);
		}
	};

	const handleProceedAfterPaymentResult = () => {
		if (paymentStatus === 'success') {
			setBookingConfirmed(true);
			setStep('confirmation');
		} else {
			setStep('payment');
			setPaymentStatus('idle');
			setPaymentError(null);
		}
	};

	const WorkshopDetails = () => (
		<div className='space-y-4'>
			<div className='space-y-4'>
				<h4 className='text-2xl font-semibold'>{workshop.title}</h4>
				<div className='flex gap-2'>
					<Badge text={`${workshop.day} ${workshop.month} ${workshop.year}`} />
					{workshop.location && <Badge text={workshop.location} />}
				</div>
				<div className='mt-6 text-base leading-5 text-neutral-800'>
					{workshop.description || 'Join us for this exciting workshop!'}
				</div>
				<div className='space-y-1'>
					<p className='text-sm font-medium text-gray-500'>PRICE</p>
					<p className='text-3xl font-bold'>R{workshop.price}</p>
				</div>
			</div>
		</div>
	);

	const renderStepContent = () => {
		switch (step) {
			case 'personal':
				return (
					<div className='space-y-8'>
						<SheetHeader className='p-0'>
							<SheetTitle className='text-3xl'>
								Workshop Registration
							</SheetTitle>
							<p className='text-sm text-gray-500'>
								Reserve your seat for this workshop.
							</p>
						</SheetHeader>

						<div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
							<div className='space-y-6'>
								<WorkshopDetails />
							</div>

							<div className='space-y-6'>
								<h3 className='text-lg font-medium'>Your Information</h3>
								<PersonalInfoForm
									data={bookingData}
									onSubmit={(data) => {
										setBookingData(data as any);
										setStep('payment');
									}}
									onBack={onClose}
								/>
							</div>
						</div>
					</div>
				);

			case 'payment':
				return (
					<WorkshopPaymentSummary
						workshop={workshop}
						bookingData={bookingData}
						onBack={() => setStep('personal')}
						onPayment={(success, message) =>
							handlePaymentResult(success, message)
						}
					/>
				);

			case 'payment-result':
				return (
					<div className='space-y-6'>
						<div>
							<h2 className='text-2xl font-semibold'>
								{paymentStatus === 'success'
									? 'Payment Complete'
									: 'Payment Failed'}
							</h2>
							<p className='text-sm text-gray-500'>
								{paymentStatus === 'success'
									? 'Your payment was processed successfully'
									: 'There was a problem processing your payment'}
							</p>
						</div>

						<Card>
							<CardHeader>
								<div className='flex items-center gap-3'>
									{paymentStatus === 'success' ? (
										<CheckCircle2 className='h-6 w-6 text-green-600' />
									) : (
										<AlertCircle className='h-6 w-6 text-red-600' />
									)}
									<CardTitle
										className={
											paymentStatus === 'success'
												? 'text-green-700'
												: 'text-red-700'
										}
									>
										{paymentStatus === 'success'
											? 'Transaction Successful'
											: 'Transaction Failed'}
									</CardTitle>
								</div>
							</CardHeader>
							<CardContent className='space-y-4'>
								<div className='space-y-2'>
									<h3 className='font-medium'>Booking Details</h3>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Workshop</span>
										<span className='font-medium'>{workshop.title}</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Date</span>
										<span className='font-medium'>
											{`${workshop.day} ${workshop.month} ${workshop.year}`}
										</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Time</span>
										<span className='font-medium'>{workshop.time}</span>
									</div>
								</div>

								<Separator />

								<div className='space-y-2'>
									{paymentStatus === 'success' ? (
										<>
											<p>Thank you for registering for this workshop.</p>
											<p className='text-sm'>
												Your transaction has been completed successfully.
												{calendarEventCreated
													? " We've sent you a calendar invitation."
													: " We'll send you a confirmation email shortly."}
											</p>
										</>
									) : (
										<>
											<p>We couldn't process your payment at this time.</p>
											<p className='text-sm text-red-600'>
												{paymentError || 'Please try again or contact support.'}
											</p>
										</>
									)}
								</div>
							</CardContent>
							<CardFooter className='border-t pt-4'>
								<Button
									onClick={handleProceedAfterPaymentResult}
									className={`w-full ${
										paymentStatus === 'success'
											? 'bg-[#fbb040] hover:bg-[#f8c880]'
											: 'bg-gray-400 hover:bg-gray-500'
									}`}
								>
									{paymentStatus === 'success' ? 'Continue' : 'Try Again'}
								</Button>
							</CardFooter>
						</Card>
					</div>
				);

			case 'confirmation':
				return (
					<div className='space-y-6'>
						<div>
							<h2 className='text-2xl font-semibold'>Registration Confirmed</h2>
							<p className='text-sm text-gray-500'>
								Your workshop registration has been successfully completed
							</p>
						</div>

						<Card>
							<CardHeader>
								<div className='flex items-center gap-3'>
									<CheckCircle2 className='h-6 w-6 text-green-600' />
									<CardTitle className='text-green-700'>
										Booking Complete
									</CardTitle>
								</div>
							</CardHeader>
							<CardContent className='space-y-4'>
								{/* Workshop Details */}
								<div className='space-y-2'>
									<h3 className='font-medium'>Workshop Details</h3>
									<div className='flex justify-between text-sm'>
										<span className='font-medium'>{workshop.title}</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Date</span>
										<span className='font-medium'>
											{`${workshop.day} ${workshop.month} ${workshop.year}`}
										</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Time</span>
										<span className='font-medium'>{workshop.time}</span>
									</div>
									<div className='flex justify-between text-sm'>
										<span className='text-gray-500'>Location</span>
										<span className='font-medium'>{workshop.location}</span>
									</div>
								</div>

								<Separator />

								{/* Personal Details */}
								<div className='space-y-2'>
									<h3 className='font-medium'>Personal Information</h3>
									<div className='text-sm space-y-1'>
										<p>{bookingData.fullName}</p>
										<p>{bookingData.phone}</p>
										<p>{bookingData.email}</p>
									</div>
								</div>

								{calendarEventCreated && (
									<>
										<Separator />
										<div className='rounded-md bg-green-50 p-3 text-sm'>
											<p className='flex items-center gap-2 text-green-700'>
												<CheckCircle2 className='h-4 w-4' />A calendar
												invitation has been sent to your email:{' '}
												{bookingData.email}
											</p>
										</div>
									</>
								)}

								<Separator />

								{/* Calendar Export Options */}
								<CalendarExport
									title={`Yaap Workshop: ${workshop.title}`}
									description={`${workshop.description || workshop.title}\n\nRegistered by: ${bookingData.fullName}\nPhone: ${bookingData.phone}`}
									startDate={getWorkshopDate()}
									startTime={
										getWorkshopDate().getHours() +
										':' +
										getWorkshopDate().getMinutes()
									}
									duration={getWorkshopDuration()}
									location={workshop.location}
								/>
							</CardContent>
							<CardFooter className='border-t pt-4'>
								<Button
									onClick={onClose}
									className='w-full bg-[#fbb040] hover:bg-[#f8c880]'
								>
									Close
								</Button>
							</CardFooter>
						</Card>

						<div className='text-sm text-gray-500 text-center'>
							<p>Thank you for registering for our workshop.</p>
							<p>We look forward to seeing you!</p>
						</div>
					</div>
				);
		}
	};

	return (
		<Sheet open={isOpen} onOpenChange={onClose}>
			<SheetContent
				side='right'
				className='w-full max-w-[100vw] sm:max-w-[100vw] p-8 overflow-y-auto'
			>
				<div className='max-w-7xl mx-auto'>{renderStepContent()}</div>
			</SheetContent>
		</Sheet>
	);
}
